import L from "leaflet";

function esc(v) {
  return String(v ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll("\"", "&quot;");
}

function buildCrsOptions(app, selected) {
  const sel = String(selected || "").toUpperCase();
  return app.services.projections.listCRS()
    .map((crs) => `<option value="${esc(crs.code)}" ${crs.code.toUpperCase() === sel ? "selected" : ""}>${esc(crs.code)} - ${esc(crs.name)}</option>`)
    .join("");
}

function llKey(lat, lon) {
  return `${Number(lat).toFixed(7)}|${Number(lon).toFixed(7)}`;
}

export const changeDetectionTool = {
  id: "change-detection",
  name: "Change Detection",
  init(app) { this._rt = this._rt ?? createRuntime(app); },
  destroy() { this._rt?.destroy(); this._rt = null; },
  renderPanel(app, containerEl) { if (!this._rt) this._rt = createRuntime(app); this._rt.mount(containerEl); },
  serialize() { return this._rt?.snapshot?.() ?? null; },
  restore(app, data) { if (!this._rt) this._rt = createRuntime(app); this._rt.restore?.(data); },
};

function createRuntime(app) {
  const map = app.map.instance;
  const paneObjects = map.createPane("changeDetectionObjectsPane");
  paneObjects.style.zIndex = "810";
  const panePoints = map.createPane("changeDetectionPointsPane");
  panePoints.style.zIndex = "809";
  const objectsLayer = L.featureGroup([], { pane: "changeDetectionObjectsPane" }).addTo(map);
  const pointsLayer = L.featureGroup([], { pane: "changeDetectionPointsPane" }).addTo(map);

  let container = null;
  let pointsVisible = true;
  let lastClusters = [];
  let sourceCRS = "EPSG:2273";
  let primaryId = "";
  let secondaryId = "";
  let soundingCatalog = [];
  let offCatalog = null;
  let inGlobalSync = false;
  let pointsGlobalId = null;
  let objectsGlobalId = null;

  const el = {};

  function $id(id) { return container?.querySelector?.(`#${id}`) || null; }
  function setSummary(html) { if (el.summary) el.summary.innerHTML = html; }
  function setTopList(html) { if (el.topList) el.topList.innerHTML = html; }

  function getLayerById(id) {
    return soundingCatalog.find((l) => l.globalLayerId === id || l.layerId === id) || null;
  }

  function setLayerOpacity(group, opacity01, role) {
    const alpha = Math.max(0, Math.min(1, Number(opacity01) || 0));
    group.eachLayer((layer) => {
      if (!layer?.setStyle) return;
      if (role === "points") layer.setStyle({ fillOpacity: 0.7 * alpha, opacity: 0.9 * alpha });
      else layer.setStyle({ fillOpacity: 0.85 * alpha, opacity: alpha });
    });
  }

  function flaggedPointsCount() { return pointsLayer.getLayers().length; }
  function objectsCount() { return objectsLayer.getLayers().length; }

  function syncGlobalMeta() {
    if (pointsGlobalId) app.services.layers.updateLayer(pointsGlobalId, { meta: { featureCount: flaggedPointsCount() }, visible: pointsVisible });
    if (objectsGlobalId) app.services.layers.updateLayer(objectsGlobalId, { meta: { featureCount: objectsCount() } });
  }

  function registerGlobalLayers() {
    if (!pointsGlobalId) {
      pointsGlobalId = app.services.layers.addLayer("change-detection", {
        name: "CD Flagged Cells",
        kind: "overlay",
        group: "Change Detection",
        type: "managed",
        visible: pointsVisible,
        opacity: 1,
        volatile: true,
        meta: { featureCount: 0 },
        managed: {
          setVisible(v) {
            pointsVisible = !!v;
            if (pointsVisible) {
              if (!map.hasLayer(pointsLayer)) pointsLayer.addTo(map);
            } else if (map.hasLayer(pointsLayer)) map.removeLayer(pointsLayer);
            if (el.togglePointsBtn) el.togglePointsBtn.textContent = pointsVisible ? "Hide flagged cells" : "Show flagged cells";
          },
          setOpacity(v) { setLayerOpacity(pointsLayer, v, "points"); },
          getBounds() {
            const b = pointsLayer.getBounds?.();
            return b?.isValid?.() ? b : null;
          },
          bringToFront() { pointsLayer.bringToFront?.(); },
          remove() {
            if (inGlobalSync) return;
            inGlobalSync = true;
            try { clearResults(); } finally { inGlobalSync = false; }
          },
        },
      });
    }
    if (!objectsGlobalId) {
      objectsGlobalId = app.services.layers.addLayer("change-detection", {
        name: "CD Objects",
        kind: "overlay",
        group: "Change Detection",
        type: "managed",
        visible: true,
        opacity: 1,
        volatile: true,
        meta: { featureCount: 0 },
        managed: {
          setVisible(v) {
            if (v) {
              if (!map.hasLayer(objectsLayer)) objectsLayer.addTo(map);
            } else if (map.hasLayer(objectsLayer)) map.removeLayer(objectsLayer);
          },
          setOpacity(v) { setLayerOpacity(objectsLayer, v, "objects"); },
          getBounds() {
            const b = objectsLayer.getBounds?.();
            return b?.isValid?.() ? b : null;
          },
          bringToFront() { objectsLayer.bringToFront?.(); },
          remove() {
            if (inGlobalSync) return;
            inGlobalSync = true;
            try { clearResults(); } finally { inGlobalSync = false; }
          },
        },
      });
    }
    syncGlobalMeta();
  }

  function enableButtons(canAnalyze, hasResults) {
    if (el.analyzeBtn) el.analyzeBtn.disabled = !canAnalyze;
    if (el.clearBtn) el.clearBtn.disabled = !hasResults;
    if (el.togglePointsBtn) el.togglePointsBtn.disabled = !hasResults;
    if (el.exportBtn) el.exportBtn.disabled = !hasResults;
  }

  function resetLayersOnly() {
    objectsLayer.clearLayers();
    pointsLayer.clearLayers();
    lastClusters = [];
    setTopList("No objects yet.");
    syncGlobalMeta();
  }

  function bothLayersSelected() {
    return !!getLayerById(primaryId) && !!getLayerById(secondaryId) && primaryId !== secondaryId;
  }

  function clearResults() {
    resetLayersOnly();
    pointsVisible = true;
    if (!map.hasLayer(pointsLayer)) pointsLayer.addTo(map);
    if (el.togglePointsBtn) el.togglePointsBtn.textContent = "Hide flagged cells";
    enableButtons(bothLayersSelected(), false);
    setSummary("Results cleared. Select primary and secondary sounding layers, then click Analyze.");
  }

  function buildDifference(primary, secondary) {
    const secondaryByLL = new Map();
    (secondary.records || []).forEach((r) => {
      secondaryByLL.set(llKey(r.lat, r.lon), r);
    });
    const merged = [];
    let unmatchedPrimary = 0;
    (primary.records || []).forEach((a) => {
      const b = secondaryByLL.get(llKey(a.lat, a.lon));
      if (!b) {
        unmatchedPrimary += 1;
        return;
      }
      let xy = null;
      try {
        xy = app.services.projections.transformCoord([Number(a.lon), Number(a.lat)], "EPSG:4326", sourceCRS);
      } catch {
        xy = null;
      }
      if (!xy || !Number.isFinite(Number(xy[0])) || !Number.isFinite(Number(xy[1]))) return;
      merged.push({
        x: Number(xy[0]),
        y: Number(xy[1]),
        lat: Number(a.lat),
        lon: Number(a.lon),
        primaryZ: Number(a.z),
        secondaryZ: Number(b.z),
        dz: Number(b.z) - Number(a.z),
      });
    });
    return { merged, unmatchedPrimary };
  }

  function clusterPoints(flagged, radiusFt, minPts) {
    const radius = Number(radiusFt);
    const r2 = radius * radius;
    const grid = new Map();
    const cellSize = radius;
    const keyFor = (x, y) => `${Math.floor(x / cellSize)}:${Math.floor(y / cellSize)}`;
    flagged.forEach((p, idx) => {
      p.used = false;
      const key = keyFor(p.x, p.y);
      if (!grid.has(key)) grid.set(key, []);
      grid.get(key).push(idx);
    });
    function nearIndices(pt) {
      const ix = Math.floor(pt.x / cellSize);
      const iy = Math.floor(pt.y / cellSize);
      const out = [];
      for (let dx = -1; dx <= 1; dx += 1) {
        for (let dy = -1; dy <= 1; dy += 1) {
          const bucket = grid.get(`${ix + dx}:${iy + dy}`);
          if (!bucket) continue;
          out.push(...bucket);
        }
      }
      return out;
    }
    const clusters = [];
    let noiseCount = 0;
    for (let i = 0; i < flagged.length; i += 1) {
      const seed = flagged[i];
      if (seed.used) continue;
      seed.used = true;
      const stack = [seed];
      const cluster = [];
      while (stack.length) {
        const cur = stack.pop();
        cluster.push(cur);
        const candidates = nearIndices(cur);
        for (let j = 0; j < candidates.length; j += 1) {
          const q = flagged[candidates[j]];
          if (!q || q.used) continue;
          const dx = q.x - cur.x;
          const dy = q.y - cur.y;
          if ((dx * dx) + (dy * dy) <= r2) {
            q.used = true;
            stack.push(q);
          }
        }
      }
      if (cluster.length < minPts) {
        noiseCount += cluster.length;
        continue;
      }
      let sumLat = 0;
      let sumLon = 0;
      let sumDz = 0;
      let maxAbsDz = 0;
      for (const pt of cluster) {
        sumLat += pt.lat;
        sumLon += pt.lon;
        sumDz += pt.dz;
        const absDz = Math.abs(pt.dz);
        if (absDz > maxAbsDz) maxAbsDz = absDz;
      }
      const n = cluster.length;
      const meanDz = sumDz / n;
      clusters.push({
        points: cluster,
        centroidLat: sumLat / n,
        centroidLon: sumLon / n,
        meanDz,
        maxAbsDz,
        type: meanDz >= 0 ? "shoal" : "scour",
        marker: null,
        id: -1,
      });
    }
    return { clusters, noiseCount };
  }

  function buildKml(clusters) {
    let kml = "";
    kml += "<?xml version=\"1.0\" encoding=\"UTF-8\"?>\n";
    kml += "<kml xmlns=\"http://www.opengis.net/kml/2.2\">\n";
    kml += "  <Document>\n";
    kml += "    <name>Change Detection Objects</name>\n";
    kml += "    <description>Clusters of cells where |dZ| exceeds the specified threshold.</description>\n";
    kml += "    <Style id=\"shoalStyle\">\n";
    kml += "      <IconStyle><color>ff00a5ff</color><scale>1.2</scale><Icon><href>http://maps.google.com/mapfiles/kml/paddle/red-circle.png</href></Icon></IconStyle>\n";
    kml += "    </Style>\n";
    kml += "    <Style id=\"scourStyle\">\n";
    kml += "      <IconStyle><color>ffffa500</color><scale>1.2</scale><Icon><href>http://maps.google.com/mapfiles/kml/paddle/ltblu-circle.png</href></Icon></IconStyle>\n";
    kml += "    </Style>\n";
    clusters.forEach((c, idx) => {
      const name = `Object ${idx + 1} - ${c.type === "shoal" ? "Shoal" : "Scour"}`;
      const desc = `Type: ${c.type === "shoal" ? "Shoaling" : "Scour"}\nCells: ${c.points.length}\nMax |dZ|: ${c.maxAbsDz.toFixed(2)} ft\nMean dZ: ${c.meanDz.toFixed(2)} ft`;
      kml += "    <Placemark>\n";
      kml += `      <name>${esc(name)}</name>\n`;
      kml += `      <description><![CDATA[${desc}]]></description>\n`;
      kml += `      <styleUrl>#${c.type === "shoal" ? "shoalStyle" : "scourStyle"}</styleUrl>\n`;
      kml += `      <Point><coordinates>${c.centroidLon.toFixed(8)},${c.centroidLat.toFixed(8)},0</coordinates></Point>\n`;
      kml += "    </Placemark>\n";
    });
    kml += "  </Document>\n";
    kml += "</kml>\n";
    return kml;
  }

  function download(filename, blob) {
    const a = document.createElement("a");
    const u = URL.createObjectURL(blob);
    a.href = u;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      URL.revokeObjectURL(u);
      a.remove();
    }, 250);
  }

  async function analyze() {
    const primary = getLayerById(primaryId);
    const secondary = getLayerById(secondaryId);
    if (!primary || !secondary || primaryId === secondaryId) return;
    registerGlobalLayers();

    const threshold = Number(el.threshold?.value);
    const radiusFt = Number(el.radius?.value);
    const minPts = Number.parseInt(el.minPts?.value, 10);
    if (!Number.isFinite(threshold) || threshold <= 0) { setSummary("Please enter a positive change threshold (ft)."); return; }
    if (!Number.isFinite(radiusFt) || radiusFt <= 0) { setSummary("Please enter a positive cluster radius (ft)."); return; }
    if (!Number.isInteger(minPts) || minPts < 1) { setSummary("Please enter a positive integer for minimum cells per object."); return; }

    setSummary(`Comparing <strong>${esc(primary.name)}</strong> to <strong>${esc(secondary.name)}</strong> (dZ = secondary - primary)...`);
    resetLayersOnly();
    enableButtons(false, false);

    try {
      const { merged, unmatchedPrimary } = buildDifference(primary, secondary);
      if (!merged.length) {
        setSummary("No overlapping sounding positions found between selected layers.");
        enableButtons(true, false);
        return;
      }

      const flagged = merged.filter((p) => Math.abs(p.dz) >= threshold);
      if (!flagged.length) {
        setSummary(`Compared ${merged.length.toLocaleString()} overlapping points at |dZ| >= ${threshold.toFixed(2)} ft: <strong>no changes above threshold detected</strong>.`);
        enableButtons(true, false);
        return;
      }

      flagged.forEach((p) => {
        const color = p.dz >= 0 ? "#f97316" : "#3b82f6";
        L.circleMarker([p.lat, p.lon], {
          radius: 2,
          weight: 0,
          fillOpacity: 0.7,
          opacity: 0.9,
          color,
        }).addTo(pointsLayer).bindTooltip(`dZ: ${p.dz.toFixed(2)} ft`, { direction: "top", sticky: true });
      });

      const { clusters, noiseCount } = clusterPoints(flagged, radiusFt, minPts);
      let shoalCount = 0;
      let scourCount = 0;
      let maxShoal = null;
      let maxScour = null;
      let bounds = null;
      clusters.forEach((c, idx) => {
        c.id = idx;
        const isShoal = c.type === "shoal";
        const color = isShoal ? "#f97316" : "#3b82f6";
        if (isShoal) {
          shoalCount += 1;
          if (maxShoal === null || c.maxAbsDz > maxShoal) maxShoal = c.maxAbsDz;
        } else {
          scourCount += 1;
          if (maxScour === null || c.maxAbsDz > maxScour) maxScour = c.maxAbsDz;
        }
        const marker = L.circleMarker([c.centroidLat, c.centroidLon], {
          radius: 6,
          weight: 1.2,
          color,
          fillColor: color,
          fillOpacity: 0.85,
        }).addTo(objectsLayer);
        marker.bindTooltip(`<strong>Change object</strong><br/>Type: ${isShoal ? "Shoaling" : "Scour"}<br/>Cells: ${c.points.length}<br/>Max |dZ|: ${c.maxAbsDz.toFixed(2)} ft<br/>Mean dZ: ${c.meanDz.toFixed(2)} ft`, { direction: "top", sticky: true });
        c.marker = marker;
        if (!bounds) bounds = L.latLngBounds([c.centroidLat, c.centroidLon], [c.centroidLat, c.centroidLon]);
        else bounds.extend([c.centroidLat, c.centroidLon]);
      });
      lastClusters = clusters;

      if (el.zoom?.checked && bounds?.isValid?.()) map.flyToBounds(bounds.pad(0.2), { duration: 2.5, easeLinearity: 0.25 });

      const totalFlag = flagged.length;
      const usedFlagged = totalFlag - noiseCount;
      let html = "";
      html += `Primary points: <strong>${(primary.records?.length || 0).toLocaleString()}</strong>; Secondary points: <strong>${(secondary.records?.length || 0).toLocaleString()}</strong>.<br/>`;
      html += `Overlapping points compared: <strong>${merged.length.toLocaleString()}</strong>; unmatched primary points: <strong>${unmatchedPrimary.toLocaleString()}</strong>.<br/>`;
      html += `Threshold: <strong>|dZ| >= ${threshold.toFixed(2)} ft</strong> (dZ = secondary - primary).<br/>`;
      html += `Flagged points above threshold: <strong>${totalFlag.toLocaleString()}</strong>.<br/>`;
      html += `Min points per object: <strong>${minPts}</strong> (DBSCAN-style).<br/>`;
      html += `Points used in objects: <strong>${usedFlagged.toLocaleString()}</strong>; noise points discarded: <strong>${noiseCount.toLocaleString()}</strong>.<br/>`;
      html += `Detected <strong>${clusters.length}</strong> change object${clusters.length === 1 ? "" : "s"}.<br/>`;
      html += "<ul style=\"margin:6px 0 0 16px;padding:0;font-size:11px;\">";
      html += `<li>Shoaling objects: <strong>${shoalCount}</strong>${maxShoal !== null ? ` (max |dZ| ~= ${maxShoal.toFixed(2)} ft)` : ""}</li>`;
      html += `<li>Scour objects: <strong>${scourCount}</strong>${maxScour !== null ? ` (max |dZ| ~= ${maxScour.toFixed(2)} ft)` : ""}</li>`;
      html += "</ul>";
      setSummary(html);

      if (!clusters.length) {
        setTopList(`No objects above threshold with at least ${minPts} points.`);
      } else {
        const sorted = clusters.slice().sort((a, b) => b.maxAbsDz - a.maxAbsDz);
        const topN = sorted.slice(0, 10);
        let listHtml = "<ol style=\"margin:4px 0 0 16px;padding:0;font-size:11px;\">";
        topN.forEach((c) => {
          const label = c.type === "shoal" ? "Shoal" : "Scour";
          listHtml += `<li style="margin-bottom:2px;"><a href="#" data-cd-idx="${c.id}" style="color:#e5e7eb;text-decoration:underline;">${label} | max |dZ| ~= ${c.maxAbsDz.toFixed(2)} ft (points: ${c.points.length})</a></li>`;
        });
        listHtml += "</ol>";
        setTopList(listHtml);
      }

      pointsVisible = true;
      if (!map.hasLayer(pointsLayer)) pointsLayer.addTo(map);
      if (el.togglePointsBtn) el.togglePointsBtn.textContent = "Hide flagged cells";
      enableButtons(true, clusters.length > 0);
      syncGlobalMeta();
    } catch (err) {
      console.error("[ChangeDetection] analyze failed", err);
      setSummary(`Error during analysis: ${esc(err?.message || String(err))}`);
      resetLayersOnly();
      enableButtons(true, false);
    }
  }

  function renderPrimarySecondaryOptions() {
    if (!container) return;
    const rows = soundingCatalog
      .map((l) => `<option value="${esc(l.globalLayerId || l.layerId)}">${esc(l.name)} (${Number(l.featureCount || 0).toLocaleString()} pts)</option>`)
      .join("");
    if (el.primarySel) {
      el.primarySel.innerHTML = `<option value="">Select primary...</option>${rows}`;
      if (primaryId) el.primarySel.value = primaryId;
    }
    if (el.secondarySel) {
      el.secondarySel.innerHTML = `<option value="">Select secondary...</option>${rows}`;
      if (secondaryId) el.secondarySel.value = secondaryId;
    }
    if (!getLayerById(primaryId)) primaryId = "";
    if (!getLayerById(secondaryId)) secondaryId = "";
    if (el.primarySel && primaryId) el.primarySel.value = primaryId;
    if (el.secondarySel && secondaryId) el.secondarySel.value = secondaryId;
    enableButtons(bothLayersSelected(), lastClusters.length > 0);
  }

  function onClick(e) {
    const t = e.target;
    if (!t) return;
    if (t.id === "cd-analyze") { analyze(); return; }
    if (t.id === "cd-clear") { clearResults(); return; }
    if (t.id === "cd-toggle-points") {
      if (t.disabled) return;
      pointsVisible = !pointsVisible;
      if (pointsVisible) {
        if (!map.hasLayer(pointsLayer)) pointsLayer.addTo(map);
        t.textContent = "Hide flagged cells";
      } else {
        if (map.hasLayer(pointsLayer)) map.removeLayer(pointsLayer);
        t.textContent = "Show flagged cells";
      }
      syncGlobalMeta();
      return;
    }
    if (t.id === "cd-export") {
      if (!lastClusters.length) {
        app.ui.toast("No change objects to export");
        return;
      }
      const kml = buildKml(lastClusters);
      download("change_detection_objects.kml", new Blob([kml], { type: "application/vnd.google-earth.kml+xml" }));
      return;
    }
    const link = t.closest?.("a[data-cd-idx]");
    if (!link) return;
    e.preventDefault();
    const idx = Number.parseInt(link.getAttribute("data-cd-idx"), 10);
    if (!Number.isInteger(idx) || !lastClusters[idx]) return;
    const c = lastClusters[idx];
    map.flyTo([c.centroidLat, c.centroidLon], Math.max(map.getZoom(), 17), { duration: 1.5, easeLinearity: 0.25 });
    if (c.marker) {
      c.marker.bringToFront();
      if (c.marker.getTooltip()) c.marker.openTooltip();
    }
  }

  function onChange(e) {
    const t = e.target;
    if (!t) return;
    if (t.id === "cd-source-crs") {
      sourceCRS = t.value || "EPSG:2273";
      return;
    }
    if (t.id === "cd-primary-layer") primaryId = t.value || "";
    if (t.id === "cd-secondary-layer") secondaryId = t.value || "";
    if (t.id === "cd-primary-layer" || t.id === "cd-secondary-layer") {
      resetLayersOnly();
      enableButtons(bothLayersSelected(), false);
      if (!bothLayersSelected()) setSummary("Select two different sounding layers, then click Analyze.");
      else {
        const p = getLayerById(primaryId);
        const s = getLayerById(secondaryId);
        setSummary(`Primary: <strong>${esc(p?.name || "")}</strong>; Secondary: <strong>${esc(s?.name || "")}</strong>. Click <strong>Analyze</strong> to compute dZ = secondary - primary.`);
      }
    }
  }

  function render() {
    if (!container) return;
    const crsOptions = buildCrsOptions(app, sourceCRS);
    container.innerHTML = `
      <div class="card">
        <div class="row" style="gap:8px;flex-wrap:wrap;align-items:flex-end;">
          <div style="flex:1 1 170px;">
            <label class="muted">Primary sounding layer</label><br/>
            <select id="cd-primary-layer" style="width:100%;"></select>
          </div>
          <div style="flex:1 1 170px;">
            <label class="muted">Secondary sounding layer</label><br/>
            <select id="cd-secondary-layer" style="width:100%;"></select>
          </div>
        </div>

        <div class="row" style="gap:8px;flex-wrap:wrap;align-items:flex-end;margin-top:6px;">
          <div>
            <label class="muted">Planar CRS</label><br/>
            <select id="cd-source-crs" style="max-width:220px;">${crsOptions}</select>
          </div>
          <div>
            <label class="muted">Change threshold</label><br/>
            <div class="row" style="gap:4px;align-items:center;">
              <input type="number" id="cd-threshold" value="1" step="0.1" min="0" style="width:70px;"/>
              <span class="muted cd-unit">ft (|dZ| >= threshold)</span>
            </div>
          </div>
          <div>
            <label class="muted">Cluster radius</label><br/>
            <div class="row" style="gap:4px;align-items:center;">
              <input type="number" id="cd-radius" value="30" step="1" min="1" style="width:70px;"/>
              <span class="muted cd-unit">ft (plan view)</span>
            </div>
          </div>
          <div>
            <label class="muted">Min points/object</label><br/>
            <div class="row" style="gap:4px;align-items:center;">
              <input type="number" id="cd-minpts" value="5" step="1" min="1" style="width:70px;"/>
              <span class="muted cd-unit">density filter</span>
            </div>
          </div>
        </div>

        <div class="row" style="margin-top:6px;gap:8px;flex-wrap:wrap;align-items:center;">
          <button id="cd-analyze" class="btn primary" disabled>Analyze</button>
          <button id="cd-clear" class="btn" disabled>Clear</button>
          <button id="cd-export" class="btn" disabled>Export objects (KML)</button>
          <label style="display:flex;align-items:center;gap:6px;font-size:12px;margin-left:auto;">
            <input type="checkbox" id="cd-zoom" checked />
            <span class="muted">Zoom to detected objects</span>
          </label>
          <button id="cd-toggle-points" class="btn" disabled>Hide flagged cells</button>
        </div>

        <div class="field" style="margin-top:8px;">
          <label>Summary</label>
          <div id="cd-summary" class="muted cd-output">Select two different sounding layers, then click <strong>Analyze</strong>.</div>
        </div>

        <div class="field" style="margin-top:8px;">
          <label>Top change objects</label>
          <div id="cd-toplist" class="muted cd-output">No objects yet.</div>
        </div>
      </div>
    `;
    el.primarySel = $id("cd-primary-layer");
    el.secondarySel = $id("cd-secondary-layer");
    el.sourceCRS = $id("cd-source-crs");
    el.threshold = $id("cd-threshold");
    el.radius = $id("cd-radius");
    el.minPts = $id("cd-minpts");
    el.analyzeBtn = $id("cd-analyze");
    el.clearBtn = $id("cd-clear");
    el.zoom = $id("cd-zoom");
    el.togglePointsBtn = $id("cd-toggle-points");
    el.exportBtn = $id("cd-export");
    el.summary = $id("cd-summary");
    el.topList = $id("cd-toplist");
    renderPrimarySecondaryOptions();
  }

  function mount(next) {
    if (container === next) return;
    container = next;
    render();
    container.addEventListener("click", onClick);
    container.addEventListener("change", onChange);
    offCatalog = app.events.on("soundings:catalog:changed", ({ layers } = {}) => {
      soundingCatalog = Array.isArray(layers) ? layers : [];
      renderPrimarySecondaryOptions();
    });
    app.events.emit("soundings:catalog:request");
  }

  function snapshot() {
    return {
      sourceCRS,
      primaryId,
      secondaryId,
      threshold: Number(el.threshold?.value || 1),
      radius: Number(el.radius?.value || 30),
      minPts: Number.parseInt(el.minPts?.value || "5", 10),
      zoomTo: !!el.zoom?.checked,
    };
  }

  function restore(data) {
    if (!data || typeof data !== "object") return;
    sourceCRS = String(data.sourceCRS || sourceCRS);
    primaryId = String(data.primaryId || primaryId);
    secondaryId = String(data.secondaryId || secondaryId);
    if (el.sourceCRS) el.sourceCRS.value = sourceCRS;
    if (el.threshold && Number.isFinite(Number(data.threshold))) el.threshold.value = `${Number(data.threshold)}`;
    if (el.radius && Number.isFinite(Number(data.radius))) el.radius.value = `${Number(data.radius)}`;
    if (el.minPts && Number.isFinite(Number(data.minPts))) el.minPts.value = `${Math.max(1, Number.parseInt(data.minPts, 10) || 1)}`;
    if (el.zoom) el.zoom.checked = data.zoomTo !== false;
    renderPrimarySecondaryOptions();
  }

  function destroy() {
    if (container) {
      container.removeEventListener("click", onClick);
      container.removeEventListener("change", onChange);
    }
    try { offCatalog?.(); } catch {}
    offCatalog = null;
    inGlobalSync = true;
    try {
      if (pointsGlobalId) app.services.layers.removeLayer(pointsGlobalId);
      if (objectsGlobalId) app.services.layers.removeLayer(objectsGlobalId);
    } catch {}
    inGlobalSync = false;
    pointsGlobalId = null;
    objectsGlobalId = null;
    pointsLayer.clearLayers();
    objectsLayer.clearLayers();
    try { map.removeLayer(pointsLayer); } catch {}
    try { map.removeLayer(objectsLayer); } catch {}
    container = null;
  }

  return { mount, destroy, snapshot, restore };
}
